import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getUnwoundDeals, reverseUnwindDeal } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { XCircle, Eye, Loader2, DollarSign, Search, RotateCcw, ArchiveRestore } from 'lucide-react';

const ALL = '__all__';

export const UnwindsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data, setData] = useState({ deals: [], stats: { total_unwound: 0, total_amount: 0 }, filters: {} });
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState(ALL);
  const [salesperson, setSalesperson] = useState(ALL);
  const [financeManager, setFinanceManager] = useState(ALL);
  const [closer, setCloser] = useState(ALL);
  const [lender, setLender] = useState(ALL);
  const [origFrom, setOrigFrom] = useState('');
  const [origTo, setOrigTo] = useState('');
  const [unwFrom, setUnwFrom] = useState('');
  const [unwTo, setUnwTo] = useState('');

  const loadDeals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (reason !== ALL) params.reason = reason;
      if (salesperson !== ALL) params.salesperson = salesperson;
      if (financeManager !== ALL) params.finance_manager = financeManager;
      if (closer !== ALL) params.closer = closer;
      if (lender !== ALL) params.lender = lender;
      if (origFrom) params.original_date_from = origFrom;
      if (origTo) params.original_date_to = origTo;
      if (unwFrom) params.unwind_date_from = unwFrom;
      if (unwTo) params.unwind_date_to = unwTo;

      const res = await getUnwoundDeals(params);
      setData(res.data || { deals: [], stats: { total_unwound: 0, total_amount: 0 }, filters: {} });
    } catch (err) {
      toast.error('Failed to load unwound deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
  }, [reason, salesperson, financeManager, closer, lender, origFrom, origTo, unwFrom, unwTo]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadDeals();
  };

  const clearFilters = () => {
    setSearch('');
    setReason(ALL);
    setSalesperson(ALL);
    setFinanceManager(ALL);
    setCloser(ALL);
    setLender(ALL);
    setOrigFrom('');
    setOrigTo('');
    setUnwFrom('');
    setUnwTo('');
  };

  const handleReverse = async (dealId, dealNumber) => {
    if (!window.confirm(`Reverse unwind for deal #${dealNumber}? The deal will return to the active pipeline. This is an admin-only action.`)) return;
    setReversing(dealId);
    try {
      await reverseUnwindDeal(dealId);
      toast.success(`Deal #${dealNumber} restored to ACTIVE`);
      loadDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse unwind');
    } finally {
      setReversing(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
  };

  const deals = data.deals || [];
  const filterOpts = data.filters || {};

  const hasActiveFilter = useMemo(() => (
    !!(search || (reason !== ALL) || (salesperson !== ALL) || (financeManager !== ALL) || (closer !== ALL) || (lender !== ALL) || origFrom || origTo || unwFrom || unwTo)
  ), [search, reason, salesperson, financeManager, closer, lender, origFrom, origTo, unwFrom, unwTo]);

  return (
    <Layout>
      <Header
        title="Unwinds"
        subtitle={`${data.stats?.total_unwound || 0} unwound deal${data.stats?.total_unwound === 1 ? '' : 's'} · $${(data.stats?.total_amount || 0).toLocaleString()} original amount`}
      />

      <div className="p-6 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-slate-200 p-4" data-testid="unwinds-filters">
          <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="unwinds-search-input"
                  className="pl-9"
                  placeholder="Deal #, customer, vehicle"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="unwinds-reason-filter"><SelectValue placeholder="All reasons" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All reasons</SelectItem>
                  {(filterOpts.reasons || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Salesperson</label>
              <Select value={salesperson} onValueChange={setSalesperson}>
                <SelectTrigger data-testid="unwinds-salesperson-filter"><SelectValue placeholder="All salespeople" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {(filterOpts.salespersons || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Finance Manager</label>
              <Select value={financeManager} onValueChange={setFinanceManager}>
                <SelectTrigger data-testid="unwinds-fm-filter"><SelectValue placeholder="All FMs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {(filterOpts.finance_managers || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Closer</label>
              <Select value={closer} onValueChange={setCloser}>
                <SelectTrigger data-testid="unwinds-closer-filter"><SelectValue placeholder="All closers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {(filterOpts.closers || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Lender</label>
              <Select value={lender} onValueChange={setLender}>
                <SelectTrigger data-testid="unwinds-lender-filter"><SelectValue placeholder="All lenders" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {(filterOpts.lenders || []).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Original Deal Date</label>
              <div className="flex gap-2">
                <Input type="date" value={origFrom} onChange={(e) => setOrigFrom(e.target.value)} data-testid="unwinds-orig-from" />
                <Input type="date" value={origTo} onChange={(e) => setOrigTo(e.target.value)} data-testid="unwinds-orig-to" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Unwind Date</label>
              <div className="flex gap-2">
                <Input type="date" value={unwFrom} onChange={(e) => setUnwFrom(e.target.value)} data-testid="unwinds-unw-from" />
                <Input type="date" value={unwTo} onChange={(e) => setUnwTo(e.target.value)} data-testid="unwinds-unw-to" />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" data-testid="unwinds-apply-search-btn">Apply</Button>
              {hasActiveFilter && (
                <Button type="button" variant="outline" onClick={clearFilters} data-testid="unwinds-clear-filters-btn">
                  <RotateCcw size={14} className="mr-1" />Clear
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <Loader2 size={32} className="mx-auto animate-spin text-slate-400 mb-2" />
            <p className="text-slate-500">Loading unwound deals...</p>
          </div>
        ) : deals.length === 0 ? (
          <div className="bg-white border border-slate-200 p-12 text-center" data-testid="unwinds-empty">
            <XCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No Unwound Deals</h3>
            <p className="text-slate-500 mb-4">
              Deals that are unwound will appear here. Unwound deals are preserved for historical tracking but excluded from active and funded metrics.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Dashboard</Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="unwinds-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Deal #</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Lender</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Salesperson</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Finance Manager</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Closer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Original Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Unwind Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Days in Process</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unwind-row-${deal.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-slate-900">{deal.deal_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{deal.customer_name || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.vehicle_info || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.lender || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.sales_person || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.finance_manager || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.closer || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-slate-700">
                          <DollarSign size={14} />
                          <span className="font-semibold">{(deal.amount_financed || 0).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmt(deal.deal_date)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500" data-testid={`unwind-date-${deal.id}`}>{fmt(deal.unwound_at)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{deal.days_in_process ?? 0}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200" data-testid={`unwind-reason-${deal.id}`}>
                          {deal.unwind_reason_display || deal.unwind_reason || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/deal/${deal.id}`)}
                            data-testid={`view-unwound-${deal.id}`}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReverse(deal.id, deal.deal_number)}
                              disabled={reversing === deal.id}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`reverse-unwind-${deal.id}`}
                            >
                              {reversing === deal.id ? (
                                <Loader2 size={14} className="mr-1 animate-spin" />
                              ) : (
                                <ArchiveRestore size={14} className="mr-1" />
                              )}
                              Restore
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UnwindsPage;
